import { z } from 'zod';

// ============================================================================
// TYPES DE BASE ET UTILITAIRES
// ============================================================================

/**
 * Type pour les réponses d'API standardisées
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Type pour les erreurs d'API
 */
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, any>;
}

/**
 * Type pour les métadonnées de pagination
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Type pour les réponses paginées
 */
export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  meta: PaginationMeta;
}

// ============================================================================
// GOOGLE REVIEWS - Types et Schémas Zod
// ============================================================================

/**
 * Schéma Zod pour un avis Google
 */
export const GoogleReviewSchema = z.object({
  author_name: z.string().min(1, "Le nom de l'auteur est requis"),
  rating: z.number().min(1).max(5, "La note doit être entre 1 et 5"),
  text: z.string().optional(),
  time: z.string().min(1, "La date est requise"),
  profile_photo_url: z.string().url().optional(),
});

/**
 * Interface pour les avis Google
 */
export interface GoogleReview extends z.infer<typeof GoogleReviewSchema> {}

/**
 * Schéma Zod pour les informations de l'entreprise Google
 */
export const GoogleBusinessInfoSchema = z.object({
  name: z.string().min(1, "Le nom de l'entreprise est requis"),
  rating: z.number().min(0).max(5),
  reviews_count: z.number().min(0),
  reviews: z.array(GoogleReviewSchema),
  place_id: z.string().min(1, "L'ID du lieu est requis"),
  address: z.string().optional(),
  website: z.string().url().optional(),
  phone: z.string().optional(),
  url: z.string().url(),
});

/**
 * Interface pour les informations de l'entreprise Google
 */
export interface GoogleBusinessInfo extends z.infer<typeof GoogleBusinessInfoSchema> {}

// ============================================================================
// DATAFORSEO - Types et Schémas Zod
// ============================================================================

/**
 * Schéma Zod pour les paramètres DataForSEO
 */
export const DataForSEOSettingsSchema = z.object({
  login: z.string().min(1, "Le login DataForSEO est requis"),
  api_key: z.string().min(1, "La clé API DataForSEO est requise"),
});

/**
 * Interface pour les paramètres DataForSEO
 */
export interface DataForSEOSettings extends z.infer<typeof DataForSEOSettingsSchema> {}

/**
 * Schéma Zod pour les mots-clés classés
 */
export const RankedKeywordDataSchema = z.object({
  keyword: z.string().min(1, "Le mot-clé est requis"),
  rank_group: z.number().min(0),
  rank_absolute: z.number().min(0),
  url: z.string().url(),
  search_volume: z.number().min(0),
  cpc: z.number().min(0).optional(),
  competition: z.number().min(0).max(1).optional(),
  etv: z.number().min(0).optional(),
  estimated_paid_traffic_cost: z.number().min(0).optional(),
  title: z.string().optional(),
  description: z.string().optional(),
});

/**
 * Interface pour les mots-clés classés
 */
export interface RankedKeywordData extends z.infer<typeof RankedKeywordDataSchema> {}

/**
 * Schéma Zod pour les données de mots-clés simplifiées
 */
export const KeywordDataSchema = z.object({
  keyword: z.string().min(1, "Le mot-clé est requis"),
  position: z.number().min(0),
  search_volume: z.number().min(0),
  url: z.string().url().optional(),
});

/**
 * Interface pour les données de mots-clés simplifiées
 */
export interface KeywordData extends z.infer<typeof KeywordDataSchema> {}

/**
 * Schéma Zod pour les résultats d'analyse de mots-clés
 */
export const KeywordAnalysisResultSchema = z.object({
  prospect_id: z.string().uuid(),
  domain: z.string().min(1),
  ranked_keywords: z.array(RankedKeywordDataSchema),
  analyzed_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

/**
 * Interface pour les résultats d'analyse de mots-clés
 */
export interface KeywordAnalysisResult extends z.infer<typeof KeywordAnalysisResultSchema> {}

// ============================================================================
// OPENAI - Types et Schémas Zod
// ============================================================================

/**
 * Schéma Zod pour la configuration OpenAI
 */
export const OpenAIConfigSchema = z.object({
  model: z.string().min(1, "Le modèle est requis").default('gpt-4o-mini'),
  temperature: z.number().min(0).max(2).default(0.7),
  max_tokens: z.number().min(1).max(4000).default(500),
});

/**
 * Interface pour la configuration OpenAI
 */
export interface OpenAIConfig extends z.infer<typeof OpenAIConfigSchema> {}

/**
 * Schéma Zod pour les paramètres OpenAI
 */
export const OpenAISettingsSchema = z.object({
  api_key: z.string().min(1, "La clé API OpenAI est requise"),
  model: z.string().default('gpt-4o-mini'),
  temperature: z.number().min(0).max(2).default(0.7),
  max_tokens: z.number().min(1).max(4000).default(500),
});

/**
 * Interface pour les paramètres OpenAI
 */
export interface OpenAISettings extends z.infer<typeof OpenAISettingsSchema> {}

/**
 * Schéma Zod pour les réponses OpenAI
 */
export const OpenAIResponseSchema = z.object({
  recommendations: z.string().optional(),
  choices: z.array(z.object({
    message: z.object({
      content: z.string(),
    }),
  })).optional(),
  error: z.object({
    message: z.string(),
  }).optional(),
});

/**
 * Interface pour les réponses OpenAI
 */
export interface OpenAIResponse extends z.infer<typeof OpenAIResponseSchema> {}

// ============================================================================
// PAGESPEED - Types et Schémas Zod
// ============================================================================

/**
 * Schéma Zod pour les paramètres PageSpeed Insights
 */
export const PageSpeedInsightsParamsSchema = z.object({
  url: z.string().url("L'URL doit être valide"),
  strategy: z.enum(['mobile', 'desktop']).optional(),
  locale: z.string().optional(),
  category: z.array(z.enum(['performance', 'accessibility', 'best-practices', 'seo', 'pwa'])).optional(),
});

/**
 * Interface pour les paramètres PageSpeed Insights
 */
export interface PageSpeedInsightsParams extends z.infer<typeof PageSpeedInsightsParamsSchema> {}

/**
 * Schéma Zod pour les scores de catégorie PageSpeed
 */
export const PageSpeedCategoryScoreSchema = z.object({
  id: z.string(),
  title: z.string(),
  score: z.number().min(0).max(1),
});

/**
 * Interface pour les scores de catégorie PageSpeed
 */
export interface PageSpeedCategoryScore extends z.infer<typeof PageSpeedCategoryScoreSchema> {}

/**
 * Schéma Zod pour les résultats PageSpeed
 */
export const PageSpeedResultSchema = z.object({
  url: z.string().url(),
  performance_score: z.number().min(0).max(1),
  accessibility_score: z.number().min(0).max(1),
  best_practices_score: z.number().min(0).max(1),
  seo_score: z.number().min(0).max(1),
  mobile_performance_score: z.number().min(0).max(1),
  desktop_performance_score: z.number().min(0).max(1),
  desktop_data: z.any().optional(),
  mobile_data: z.any().optional(),
  prospect_id: z.string().uuid().optional(),
});

/**
 * Interface pour les résultats PageSpeed
 */
export interface PageSpeedResult extends z.infer<typeof PageSpeedResultSchema> {}

/**
 * Schéma Zod pour les options d'analyse PageSpeed
 */
export const PageSpeedAnalysisOptionsSchema = z.object({
  includeDesktop: z.boolean().default(true),
  includeMobile: z.boolean().default(true),
  includeCategories: z.array(z.string()).optional(),
  saveResults: z.boolean().default(true),
});

/**
 * Interface pour les options d'analyse PageSpeed
 */
export interface PageSpeedAnalysisOptions extends z.infer<typeof PageSpeedAnalysisOptionsSchema> {}

// ============================================================================
// API KEYS - Types et Schémas Zod
// ============================================================================

/**
 * Schéma Zod pour les services API supportés
 */
export const ApiServiceSchema = z.enum(['whatcms', 'openai', 'dataforseo', 'pagespeed', 'google']);

/**
 * Type pour les services API supportés
 */
export type ApiService = z.infer<typeof ApiServiceSchema>;

/**
 * Schéma Zod pour les clés API
 */
export const ApiKeySchema = z.object({
  id: z.string().uuid(),
  service: z.string().min(1),
  api_key: z.string().min(1, "La clé API est requise"),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  metadata: z.record(z.any()).optional(),
});

/**
 * Interface pour les clés API
 */
export interface ApiKey extends z.infer<typeof ApiKeySchema> {}

// ============================================================================
// CMS DETECTION - Types et Schémas Zod
// ============================================================================

/**
 * Schéma Zod pour les informations CMS
 */
export const CmsInfoSchema = z.object({
  name: z.string(),
  version: z.string().optional(),
  confidence: z.number().min(0).max(100),
  categories: z.array(z.string()).optional(),
});

/**
 * Interface pour les informations CMS
 */
export interface CmsInfo extends z.infer<typeof CmsInfoSchema> {}

/**
 * Schéma Zod pour les résultats de détection CMS
 */
export const CmsDetectionResultSchema = z.object({
  url: z.string().url(),
  cms: z.array(CmsInfoSchema),
  detected_at: z.string().datetime(),
  prospect_id: z.string().uuid().optional(),
});

/**
 * Interface pour les résultats de détection CMS
 */
export interface CmsDetectionResult extends z.infer<typeof CmsDetectionResultSchema> {}

// ============================================================================
// PROSPECTS - Types et Schémas Zod
// ============================================================================

/**
 * Schéma Zod pour les données de prospect
 */
export const ProspectDataSchema = z.object({
  id: z.string().uuid(),
  company_name: z.string().min(1, "Le nom de l'entreprise est requis"),
  website: z.string().url("L'URL du site web doit être valide"),
  email: z.string().email("L'email doit être valide").optional(),
  phone: z.string().optional(),
  contact_person: z.string().optional(),
  industry: z.string().optional(),
  status: z.enum(['new', 'contacted', 'qualified', 'converted', 'rejected']).default('new'),
  notes: z.string().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  // Données d'analyse
  pagespeed_score: z.number().min(0).max(1).optional(),
  seo_score: z.number().min(0).max(1).optional(),
  cms_detected: z.string().optional(),
  keywords_count: z.number().min(0).optional(),
  google_rating: z.number().min(0).max(5).optional(),
  google_reviews_count: z.number().min(0).optional(),
});

/**
 * Interface pour les données de prospect
 */
export interface ProspectData extends z.infer<typeof ProspectDataSchema> {}

// ============================================================================
// QUIZ - Types et Schémas Zod
// ============================================================================

/**
 * Schéma Zod pour les réponses de quiz
 */
export const QuizAnswerSchema = z.object({
  questionId: z.string(),
  answer: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]),
});

/**
 * Interface pour les réponses de quiz
 */
export interface QuizAnswer extends z.infer<typeof QuizAnswerSchema> {}

/**
 * Schéma Zod pour les résultats de quiz
 */
export const QuizResultSchema = z.object({
  id: z.string().uuid(),
  prospect_id: z.string().uuid(),
  answers: z.array(QuizAnswerSchema),
  score: z.number().min(0).max(100),
  completed_at: z.string().datetime(),
  recommendations: z.array(z.string()).optional(),
});

/**
 * Interface pour les résultats de quiz
 */
export interface QuizResult extends z.infer<typeof QuizResultSchema> {}

// ============================================================================
// CAMPAGNES EMAIL - Types et Schémas Zod
// ============================================================================

/**
 * Schéma Zod pour les campagnes email
 */
export const EmailCampaignSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1, "Le nom de la campagne est requis"),
  subject: z.string().min(1, "Le sujet est requis"),
  content: z.string().min(1, "Le contenu est requis"),
  status: z.enum(['draft', 'scheduled', 'sent', 'paused']).default('draft'),
  scheduled_at: z.string().datetime().optional(),
  sent_at: z.string().datetime().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  // Statistiques
  recipients_count: z.number().min(0).default(0),
  sent_count: z.number().min(0).default(0),
  opened_count: z.number().min(0).default(0),
  clicked_count: z.number().min(0).default(0),
  bounced_count: z.number().min(0).default(0),
});

/**
 * Interface pour les campagnes email
 */
export interface EmailCampaign extends z.infer<typeof EmailCampaignSchema> {}

// ============================================================================
// FONCTIONS DE VALIDATION
// ============================================================================

/**
 * Valide et parse des données avec un schéma Zod
 */
export function validateAndParse<T>(schema: z.ZodSchema<T>, data: unknown): { success: true; data: T } | { success: false; error: string } {
  try {
    const result = schema.parse(data);
    return { success: true, data: result };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessages = error.errors.map(err => `${err.path.join('.')}: ${err.message}`).join(', ');
      return { success: false, error: errorMessages };
    }
    return { success: false, error: 'Erreur de validation inconnue' };
  }
}

/**
 * Valide des données avec un schéma Zod (version safe)
 */
export function safeValidate<T>(schema: z.ZodSchema<T>, data: unknown): z.SafeParseReturnType<unknown, T> {
  return schema.safeParse(data);
}

/**
 * Crée un validateur pour un schéma donné
 */
export function createValidator<T>(schema: z.ZodSchema<T>) {
  return {
    validate: (data: unknown) => validateAndParse(schema, data),
    safeValidate: (data: unknown) => safeValidate(schema, data),
    parse: (data: unknown) => schema.parse(data),
    safeParse: (data: unknown) => schema.safeParse(data),
  };
}

// ============================================================================
// VALIDATEURS PRÊTS À L'EMPLOI
// ============================================================================

export const validators = {
  googleReview: createValidator(GoogleReviewSchema),
  googleBusinessInfo: createValidator(GoogleBusinessInfoSchema),
  dataForSEOSettings: createValidator(DataForSEOSettingsSchema),
  rankedKeywordData: createValidator(RankedKeywordDataSchema),
  keywordData: createValidator(KeywordDataSchema),
  keywordAnalysisResult: createValidator(KeywordAnalysisResultSchema),
  openAIConfig: createValidator(OpenAIConfigSchema),
  openAISettings: createValidator(OpenAISettingsSchema),
  openAIResponse: createValidator(OpenAIResponseSchema),
  pageSpeedInsightsParams: createValidator(PageSpeedInsightsParamsSchema),
  pageSpeedCategoryScore: createValidator(PageSpeedCategoryScoreSchema),
  pageSpeedResult: createValidator(PageSpeedResultSchema),
  pageSpeedAnalysisOptions: createValidator(PageSpeedAnalysisOptionsSchema),
  apiKey: createValidator(ApiKeySchema),
  cmsInfo: createValidator(CmsInfoSchema),
  cmsDetectionResult: createValidator(CmsDetectionResultSchema),
  prospectData: createValidator(ProspectDataSchema),
  quizAnswer: createValidator(QuizAnswerSchema),
  quizResult: createValidator(QuizResultSchema),
  emailCampaign: createValidator(EmailCampaignSchema),
};

// ============================================================================
// TYPES UTILITAIRES
// ============================================================================

/**
 * Type pour extraire le type de données d'un validateur
 */
export type ValidatorData<T> = T extends { validate: (data: unknown) => { success: true; data: infer U } | { success: false; error: string } } ? U : never;

/**
 * Type pour les options de requête API
 */
export interface ApiRequestOptions {
  timeout?: number;
  retries?: number;
  headers?: Record<string, string>;
}

/**
 * Type pour les filtres de recherche
 */
export interface SearchFilters {
  query?: string;
  status?: string[];
  dateFrom?: string;
  dateTo?: string;
  industry?: string[];
  [key: string]: any;
}

/**
 * Type pour les options de tri
 */
export interface SortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

/**
 * Type pour les options de pagination
 */
export interface PaginationOptions {
  page: number;
  limit: number;
  sort?: SortOptions;
  filters?: SearchFilters;
}
